import { Box, Divider, MenuItem, Select, Stack, Typography } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import type { WhatsappNumberRow } from "hooks/useWhatsappNumbers";
import { formatPhoneLocal } from "utils/whatsapp/formatPhone";
import WhatsAppGlyph from "./WhatsAppGlyph";

/** Sentinel value for the "connect another number" row. Not a numberKey — intercepted on change. */
const CONNECT_NEW = "__connect_new__";

/**
 * The "current active WhatsApp number" dropdown, and the only route to onboarding once the user
 * already has a number.
 *
 * The closed control is deliberately one slim line (glyph + name) via `renderValue`; the
 * two-line detail (name + local number) lives only inside the open menu. Rendering the menu
 * item's block inside the input is what made the first version tall and clumsy.
 *
 * It renders from ONE number, not two. Hiding it on a single-number account is what left a user
 * holding one number — or one that had just been disconnected — with no way back to the connect
 * flow at all, since the page only offered that card when nothing was selected.
 */
export default function NumberSwitcher({
  numbers,
  selectedKey,
  onSelect,
  onConnectNew
}: {
  numbers: WhatsappNumberRow[];
  selectedKey?: string;
  onSelect: (numberKey: string) => void;
  /** Omitted on the inbox/templates pages, which switch between numbers but do not onboard. */
  onConnectNew?: () => void;
}) {
  if (numbers.length === 0) return null;
  // Without the connect action there is nothing to offer a single-number account, so keep the
  // original "hide it" behaviour on those pages.
  if (numbers.length <= 1 && !onConnectNew) return null;

  const labelOf = (n?: WhatsappNumberRow) =>
    n?.verifiedName || formatPhoneLocal(n?.displayPhoneNumber ?? n?.numberKey);

  return (
    <Select
        size="small"
        value={selectedKey ?? ""}
        onChange={(e) => {
          const value = String(e.target.value);
          // The connect row is an ACTION, not a selection — never let it become `selectedKey`,
          // which would ask the page to render a number that does not exist.
          if (value === CONNECT_NEW) {
            onConnectNew?.();
            return;
          }
          onSelect(value);
        }}
        displayEmpty
        renderValue={(value) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <WhatsAppGlyph size={16} />
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {value ? labelOf(numbers.find((n) => n.numberKey === value)) : "חיבור מספר חדש"}
            </Typography>
          </Stack>
        )}
        sx={{
          minWidth: 170,
          maxWidth: 240,
          borderRadius: 5,
          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "& .MuiSelect-select": { py: 0.75 }
        }}
        MenuProps={{ PaperProps: { sx: { mt: 0.5, borderRadius: 2 } } }}
      >
        {numbers.map((n) => (
          <MenuItem key={n.numberKey} value={n.numberKey}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {n.verifiedName || formatPhoneLocal(n.displayPhoneNumber ?? n.numberKey)}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", textAlign: "start" }}>
                {formatPhoneLocal(n.displayPhoneNumber ?? n.numberKey)}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        {onConnectNew && <Divider sx={{ my: 0.5 }} />}
        {onConnectNew && (
          <MenuItem value={CONNECT_NEW}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1 }}>
                +
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                חיבור מספר חדש
              </Typography>
            </Stack>
          </MenuItem>
        )}
    </Select>
  );
}
